/**
 * Centralized logging utility for Homerun
 * Provides conditional logging based on environment and log levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  enableDebug: boolean;
  enableInfo: boolean;
  enableWarn: boolean;
  enableError: boolean;
  prefix?: string;
}

const isDevelopment = import.meta.env.DEV;
const isDebugLoggingEnabled = import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';

const defaultConfig: LogConfig = {
  enableDebug: isDevelopment && isDebugLoggingEnabled,
  enableInfo: true,
  enableWarn: true,
  enableError: true,
  prefix: '🎮 Homerun'
};

let config: LogConfig = { ...defaultConfig };

/**
 * Configure logger settings
 */
export const configureLogger = (customConfig: Partial<LogConfig>): void => {
  config = { ...config, ...customConfig };
};

/**
 * Reset logger to default configuration
 */
export const resetLogger = (): void => {
  config = { ...defaultConfig };
};

/**
 * Format log message with optional prefix and emoji
 */
const formatMessage = (_level: LogLevel, message: string, emoji?: string): string => {
  const prefix = config.prefix ? `${config.prefix} ` : '';
  const emojiPrefix = emoji ? `${emoji} ` : '';
  return `${prefix}${emojiPrefix}${message}`;
};

/**
 * Debug logs - only shown in development mode
 * Use for detailed troubleshooting information
 */
export const debug = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '🔍'), ...args);
  }
};

/**
 * Info logs - general information
 * Use for normal application flow events
 */
export const info = (message: string, ...args: unknown[]): void => {
  if (config.enableInfo) {
    console.info(formatMessage('info', message, '📝'), ...args);
  }
};

/**
 * Warning logs - potential issues
 * Use for recoverable errors or unexpected states
 */
export const warn = (message: string, ...args: unknown[]): void => {
  if (config.enableWarn) {
    console.warn(formatMessage('warn', message, '⚠️'), ...args);
  }
};

/**
 * Error logs - critical issues
 * Use for errors that need immediate attention
 */
export const error = (message: string, ...args: unknown[]): void => {
  if (config.enableError) {
    console.error(formatMessage('error', message, '❌'), ...args);
  }
};

/**
 * Success logs - positive confirmations
 * Use for successful operations
 */
export const success = (message: string, ...args: unknown[]): void => {
  if (config.enableInfo) {
    console.log(formatMessage('info', message, '✅'), ...args);
  }
};

/**
 * Game flow logs - state transitions
 * Use for tracking game phase changes
 */
export const gameFlow = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '🎯'), ...args);
  }
};

/**
 * Network logs - API calls and Firebase operations
 * Use for tracking network activity
 */
export const network = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '📡'), ...args);
  }
};

/**
 * Player action logs - user interactions
 * Use for tracking player actions
 */
export const player = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '👤'), ...args);
  }
};

/**
 * Scoring logs - points and calculations
 * Use for score-related events
 */
export const scoring = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '🎯'), ...args);
  }
};

/**
 * Timer logs - countdown and phase timing
 * Use for timer-related events
 */
export const timer = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '⏱️'), ...args);
  }
};

/**
 * Combat logs - items, grenades, nets
 * Use for combat system events
 */
export const combat = (message: string, ...args: unknown[]): void => {
  if (config.enableDebug) {
    console.log(formatMessage('debug', message, '💥'), ...args);
  }
};

/**
 * Group logs together
 */
export const group = (label: string, callback: () => void): void => {
  if (config.enableDebug) {
    console.group(formatMessage('debug', label));
    callback();
    console.groupEnd();
  }
};

/**
 * Table output for structured data
 */
export const table = (data: unknown, columns?: string[]): void => {
  if (config.enableDebug) {
    console.table(data, columns);
  }
};

/**
 * Performance timing
 */
export const time = (label: string): void => {
  if (config.enableDebug) {
    console.time(formatMessage('debug', label, '⏱️'));
  }
};

export const timeEnd = (label: string): void => {
  if (config.enableDebug) {
    console.timeEnd(formatMessage('debug', label, '⏱️'));
  }
};

/**
 * Centralized logger object
 */
export const logger = {
  debug,
  info,
  warn,
  error,
  success,
  gameFlow,
  network,
  player,
  scoring,
  timer,
  combat,
  group,
  table,
  time,
  timeEnd,
  configure: configureLogger,
  reset: resetLogger
};

export default logger;
